import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaEdit,
  FaEye,
  FaPlus,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import {
  Badge,
  ConfirmDialog,
  DangerButton,
  formatShortDate,
  PrimaryButton,
  SecondaryButton,
  SkeletonBoard,
  SoftButton,
  LoadingSpinner,
} from "../../UI/DesignStandards";
import {
  compareBowlers,
  nameMatchesSearch,
} from "../../../utility/bowlerName";
import {
  AverageCatalogInUseError,
  averageCatalogInUseMessage,
  deleteAverageCatalog,
  deleteLeagueSheetImport,
  extractLeagueSheetFiles,
  extractionFoundNoBowlers,
  extractionNoBowlersMessage,
  getAverageCatalog,
  listCatalogExtractedAverages,
  listLeagueSheetImports,
  listTournamentsBlockingAverageCatalogDelete,
  DEFAULT_MIN_GAMES_FOR_AVERAGE,
  averageMeetsMinimum,
  parseMinGamesForAverage,
  processLeagueSheetImport,
  updateLeagueSheetImportLeagueName,
  sheetFoundNoAverages,
  sheetNoticeMessage,
} from "../../../utility/averageCatalogs";
import {
  AVERAGE_IMPORT_PLAN_MESSAGE,
  FEATURE_AI_LEAGUE_AVERAGE_IMPORT,
  tournamentHasAiFeature,
} from "../../../utility/features";
import LeagueSheetViewer from "./LeagueSheetViewer";
import { LeagueSheetUploadForm } from "./LeagueSheetUploadForm";

const statusLabel = (row, averageRows) => {
  if (sheetFoundNoAverages(row, averageRows)) return "No averages";
  if (row.status === "uploaded") return "Uploaded";
  if (row.status === "processing") return "Processing";
  if (row.status === "completed") return "Completed";
  if (row.status === "failed") return "Failed";
  return row.status;
};

const statusVariant = (row, averageRows) => {
  if (sheetFoundNoAverages(row, averageRows)) return "warning";
  if (row.status === "completed") return "success";
  if (row.status === "failed") return "danger";
  if (row.status === "processing") return "warning";
  return "default";
};

const chipClass = (active) =>
  `max-lg:min-h-11 max-lg:inline-flex max-lg:items-center px-3 py-1.5 text-sm rounded-lg font-display font-bold transition-colors ${
    active
      ? "bg-brand-charcoal text-brand-cream"
      : "bg-white text-brand-charcoal border border-brand-charcoal/20 hover:border-brand-charcoal"
  }`;

const sheetLabel = (row) =>
  row.leagueName || row.fileName || "League sheet";

const compareNullableNumber = (left, right, direction) => {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (left !== right) return (left - right) * direction;
  return 0;
};

const compareCatalogRows = (left, right, sortBy, sortOrder) => {
  const direction = sortOrder === "desc" ? -1 : 1;
  if (sortBy === "games" || sortBy === "pins") {
    const compared = compareNullableNumber(
      left[sortBy],
      right[sortBy],
      direction
    );
    if (compared !== 0) return compared;
  }
  if (sortBy === "source") {
    const compared = (left.source || "").localeCompare(
      right.source || "",
      undefined,
      { sensitivity: "base" }
    );
    if (compared !== 0) return compared * direction;
  }
  return compareBowlers(
    {
      bowlerName: left.name,
      average: left.average,
      hasAverage: left.average != null,
    },
    {
      bowlerName: right.name,
      average: right.average,
      hasAverage: right.average != null,
    },
    sortBy === "games" || sortBy === "pins" || sortBy === "source"
      ? "lastName"
      : sortBy,
    sortOrder
  );
};

const SortableTh = ({ label, field, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  const Icon = !active ? FaSort : sortOrder === "asc" ? FaSortUp : FaSortDown;
  return (
    <th className="px-4 py-2 text-left text-xs font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal">
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1.5 hover:text-brand-gold"
        aria-sort={
          active ? (sortOrder === "asc" ? "ascending" : "descending") : "none"
        }
      >
        {label}
        <Icon
          className={`h-3 w-3 ${active ? "text-brand-gold" : "opacity-50"}`}
        />
      </button>
    </th>
  );
};

const AverageCatalogDetail = () => {
  const { catalogId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { canManageTournaments } = usePermissions();
  const organizationID = currentOrganization?.id;

  const [catalog, setCatalog] = useState(null);
  const [imports, setImports] = useState([]);
  const [averageRows, setAverageRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [retryingID, setRetryingID] = useState(null);
  const [removingID, setRemovingID] = useState(null);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [files, setFiles] = useState([]);
  const [leagueName, setLeagueName] = useState("");
  const [savingLeagueID, setSavingLeagueID] = useState(null);
  const [editingLeagueID, setEditingLeagueID] = useState(null);
  const [leagueNameDraft, setLeagueNameDraft] = useState("");
  const [extractProgress, setExtractProgress] = useState(null);
  const [search, setSearch] = useState("");
  const [sheetFilter, setSheetFilter] = useState("all");
  const [gamesFilter, setGamesFilter] = useState("all");
  const [gamesFilterMin, setGamesFilterMin] = useState(
    String(DEFAULT_MIN_GAMES_FOR_AVERAGE)
  );
  const [sortBy, setSortBy] = useState("lastName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sheetViewer, setSheetViewer] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Org extract uses null tournament_id — trial does not apply (BTA-156 Check 3).
  const [canExtract, setCanExtract] = useState(false);
  const [extractEntitlementReady, setExtractEntitlementReady] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (!organizationID || !catalogId) {
      setCatalog(null);
      setImports([]);
      setAverageRows([]);
      setLoading(false);
      return;
    }
    try {
      const [nextCatalog, nextImports, nextAverages] = await Promise.all([
        getAverageCatalog(catalogId, organizationID),
        listLeagueSheetImports(catalogId, organizationID),
        listCatalogExtractedAverages(catalogId, organizationID),
      ]);
      setCatalog(nextCatalog);
      setImports(nextImports);
      setAverageRows(nextAverages);
    } catch (err) {
      console.error(err);
      setError("Could not load this average catalog.");
    } finally {
      setLoading(false);
    }
  }, [catalogId, organizationID]);

  useEffect(() => {
    setLoading(true);
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!organizationID) {
      setCanExtract(false);
      setExtractEntitlementReady(true);
      return undefined;
    }
    let cancelled = false;
    setExtractEntitlementReady(false);
    tournamentHasAiFeature(
      organizationID,
      FEATURE_AI_LEAGUE_AVERAGE_IMPORT,
      null
    )
      .then((allowed) => {
        if (!cancelled) setCanExtract(Boolean(allowed));
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setCanExtract(false);
      })
      .finally(() => {
        if (!cancelled) setExtractEntitlementReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationID]);

  const minGamesForAverage =
    parseMinGamesForAverage(gamesFilterMin) || DEFAULT_MIN_GAMES_FOR_AVERAGE;

  const sheetFilters = useMemo(() => {
    const completed = imports.filter((row) => row.status === "completed");
    return [
      { id: "all", label: `All (${averageRows.length})` },
      ...completed.map((row) => ({
        id: row.id,
        label: `${sheetLabel(row)} (${
          averageRows.filter((average) => average.importID === row.id).length
        })`,
      })),
    ];
  }, [imports, averageRows]);

  const gamesFilters = useMemo(() => {
    const meets = averageRows.filter((row) =>
      averageMeetsMinimum(row.games, minGamesForAverage)
    ).length;
    return [
      { id: "all", label: `All (${averageRows.length})` },
      { id: "meets", label: `${minGamesForAverage}+ games (${meets})` },
      {
        id: "below",
        label: `Under ${minGamesForAverage} (${averageRows.length - meets})`,
      },
    ];
  }, [averageRows, minGamesForAverage]);

  const visibleRows = useMemo(() => {
    return averageRows
      .filter((row) => {
        if (sheetFilter !== "all" && row.importID !== sheetFilter) {
          return false;
        }
        const meetsMinimum = averageMeetsMinimum(
          row.games,
          minGamesForAverage
        );
        if (gamesFilter === "meets" && !meetsMinimum) return false;
        if (gamesFilter === "below" && meetsMinimum) return false;
        if (!nameMatchesSearch(row.name, search)) {
          const query = search.trim().toLowerCase();
          if (!query) return true;
          return [row.usbcId, row.source, row.leagueName, row.fileName]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query));
        }
        return true;
      })
      .sort((left, right) =>
        compareCatalogRows(left, right, sortBy, sortOrder)
      );
  }, [
    averageRows,
    search,
    sheetFilter,
    gamesFilter,
    minGamesForAverage,
    sortBy,
    sortOrder,
  ]);

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortOrder("asc");
  };

  const handleFileReject = (message) => {
    setError(message || "");
    setWarning("");
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!user || !organizationID || !catalogId) return;
    if (!canExtract) {
      setError(AVERAGE_IMPORT_PLAN_MESSAGE);
      setShowUpload(false);
      return;
    }
    if (files.length === 0) {
      setError("Choose one or more league sheets first.");
      return;
    }
    setExtracting(true);
    setExtractProgress({ done: 0, total: files.length });
    setError("");
    setWarning("");
    try {
      const result = await extractLeagueSheetFiles({
        catalogID: catalogId,
        organizationID,
        leagueName,
        files,
        createdBy: user.id,
        // Org path: omit tournamentID so trial cannot cover this extract.
        onProgress: setExtractProgress,
        onFileDone: loadCatalog,
      });
      setFiles([]);
      setLeagueName("");
      setShowUpload(false);
      await loadCatalog();
      if (result.emptySheets.length) {
        setWarning(result.emptySheets.join(" "));
      }
      if (result.failures.length) {
        setError(result.failures.join(" "));
      }
    } finally {
      setExtracting(false);
      setExtractProgress(null);
    }
  };

  const handleRemove = async (importRow) => {
    if (!organizationID || !importRow) return;
    setRemovingID(importRow.id);
    setError("");
    setWarning("");
    try {
      await deleteLeagueSheetImport(importRow, organizationID);
      if (sheetFilter === importRow.id) {
        setSheetFilter("all");
      }
      await loadCatalog();
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not remove this league sheet.");
    } finally {
      setRemovingID(null);
    }
  };

  const handleDeleteClick = async () => {
    if (!catalog?.id) return;
    setError("");
    try {
      const names = await listTournamentsBlockingAverageCatalogDelete(
        catalog.id
      );
      if (names.length) {
        setError(averageCatalogInUseMessage(names));
        return;
      }
      setConfirmDelete(true);
    } catch (err) {
      console.error(err);
      setError("Could not check whether this catalog can be deleted.");
    }
  };

  const handleDelete = async () => {
    if (!catalog?.id || !organizationID) return;
    setDeleting(true);
    setError("");
    try {
      await deleteAverageCatalog(catalog.id, organizationID);
      navigate("/organization/average-catalogs");
    } catch (err) {
      console.error(err);
      setConfirmDelete(false);
      setError(
        err instanceof AverageCatalogInUseError
          ? err.message
          : "Could not delete this catalog."
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleRetry = async (importRow) => {
    if (!organizationID || !importRow) return;
    if (!canExtract) {
      setError(AVERAGE_IMPORT_PLAN_MESSAGE);
      return;
    }
    setRetryingID(importRow.id);
    setError("");
    setWarning("");
    try {
      const payload = await processLeagueSheetImport(
        importRow.id,
        organizationID
      );
      await loadCatalog();
      if (extractionFoundNoBowlers(payload)) {
        setWarning(
          `${importRow.fileName || "This sheet"}: ${extractionNoBowlersMessage(
            payload
          )}`
        );
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not extract this league sheet.");
      await loadCatalog();
    } finally {
      setRetryingID(null);
    }
  };

  const startLeagueNameEdit = (importRow) => {
    setEditingLeagueID(importRow.id);
    setLeagueNameDraft(importRow.leagueName || "");
  };

  const cancelLeagueNameEdit = () => {
    setEditingLeagueID(null);
    setLeagueNameDraft("");
  };

  const handleLeagueNameSave = async (importRow) => {
    const trimmed = leagueNameDraft.trim();
    const current = importRow.leagueName || "";
    if (trimmed === current) {
      cancelLeagueNameEdit();
      return;
    }
    setSavingLeagueID(importRow.id);
    setError("");
    try {
      await updateLeagueSheetImportLeagueName({
        importID: importRow.id,
        organizationID,
        leagueName: trimmed,
      });
      cancelLeagueNameEdit();
      await loadCatalog();
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not save this league name.");
    } finally {
      setSavingLeagueID(null);
    }
  };

  const openSheetRow = (row) => {
    if (!row?.storagePath) return;
    setSheetViewer({
      title: row.name,
      subtitle: row.fileName,
      storagePath: row.storagePath,
      mimeType: row.mimeType,
      fileName: row.fileName,
      sourcePage: row.sourcePage,
      boundingBox: row.boundingBox,
      highlightAverage: row.average,
      highlightGames: row.games,
      highlightPins: row.pins,
    });
  };

  const resetUploadForm = () => {
    setFiles([]);
    setLeagueName("");
    setShowUpload(false);
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6" role="status" aria-label="Loading average catalog">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={6} />
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="space-y-4">
        <InlineAlert
          type="error"
          title="Catalog not found"
          message="This average catalog is not in the current organization."
        />
        <Link
          to="/organization/average-catalogs"
          className="text-sm font-medium text-brand-charcoal underline"
        >
          Back to average catalogs
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-brand-gold mb-1">
              Beta
            </p>
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              {catalog.name}
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              {catalog.asOfDate
                ? `As of ${formatShortDate(catalog.asOfDate)}.`
                : "No as-of date."}
            </p>
            <Link
              to="/organization/average-catalogs"
              className="inline-block mt-3 text-sm text-brand-gold underline"
            >
              Back to average catalogs
            </Link>
          </div>
          {canManageTournaments ? (
            <DangerButton
              onClick={handleDeleteClick}
              disabled={deleting}
              icon={FaTrash}
            >
              Delete catalog
            </DangerButton>
          ) : null}
        </div>
      </header>

      {error ? (
        <div className="space-y-2">
          <InlineAlert
            type="error"
            title="There was a problem"
            message={error}
            onClose={() => setError("")}
          />
          {/Open Plan/i.test(error) ? (
            <Link
              to="/organization/plan"
              className="inline-block text-sm underline text-brand-gold-ink"
            >
              Open Plan
            </Link>
          ) : null}
        </div>
      ) : null}
      {extractEntitlementReady && !canExtract && canManageTournaments ? (
        <div className="space-y-2">
          <InlineAlert
            type="info"
            title="Average import needs a plan"
            message={AVERAGE_IMPORT_PLAN_MESSAGE}
          />
          <Link
            to="/organization/plan"
            className="inline-block text-sm underline text-brand-gold-ink"
          >
            Open Plan
          </Link>
        </div>
      ) : null}
      {warning ? (
        <InlineAlert
          type="warning"
          title="No bowler averages found"
          message={warning}
          onClose={() => setWarning("")}
        />
      ) : null}

      <div className="card-board overflow-hidden min-w-0 max-w-full">
        <div className="px-6 py-3 border-b border-brand-charcoal/10 flex items-center justify-between gap-3">
          <div>
            <h2 className="heading-display text-lg">League sheets</h2>
            <p className="text-sm text-brand-muted">
              {imports.length === 1 ? "1 sheet" : `${imports.length} sheets`}
            </p>
          </div>
          {canManageTournaments &&
          canExtract &&
          !showUpload &&
          imports.length > 0 ? (
            <PrimaryButton
              type="button"
              icon={FaPlus}
              onClick={() => setShowUpload(true)}
            >
              Upload league sheets
            </PrimaryButton>
          ) : null}
        </div>
        {retryingID ? (
          <div
            className="px-6 py-3 border-b border-brand-charcoal/10 flex items-center gap-3"
            role="status"
            aria-live="polite"
          >
            <LoadingSpinner size="lg" />
            <p className="text-sm text-brand-charcoal">
              Extracting this league sheet. This can take a minute.
            </p>
          </div>
        ) : null}
        {canManageTournaments &&
        canExtract &&
        (showUpload || imports.length === 0) ? (
          <div
            className={`px-6 py-4${
              imports.length ? " border-b border-brand-charcoal/10" : ""
            }`}
          >
            <LeagueSheetUploadForm
              files={files}
              leagueName={leagueName}
              onFilesChange={setFiles}
              onLeagueNameChange={setLeagueName}
              onReject={handleFileReject}
              onSubmit={handleUpload}
              onCancel={resetUploadForm}
              extracting={extracting}
              extractProgress={extractProgress}
              showCancel={imports.length > 0}
            />
          </div>
        ) : null}
        {imports.length === 0 ? (
          canManageTournaments &&
          (!extractEntitlementReady || canExtract) ? null : (
            <p className="px-6 py-4 text-sm text-brand-muted">
              {canManageTournaments && extractEntitlementReady && !canExtract
                ? "Upload and extract need AI Assist or Priority."
                : "No league sheets in this catalog yet."}
            </p>
          )
        ) : (
          <div className="max-h-48 max-w-full overflow-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="sticky top-0 bg-brand-charcoal/5 text-brand-charcoal">
                <tr>
                  <th className="px-6 py-2 font-display font-bold">League</th>
                  <th className="px-6 py-2 font-display font-bold">File</th>
                  <th className="px-6 py-2 font-display font-bold">Status</th>
                  {canManageTournaments ? (
                    <th className="px-6 py-2 font-display font-bold">
                      <span className="sr-only">Actions</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {imports.map((row) => {
                  const notice = sheetNoticeMessage(row, averageRows);
                  return (
                  <tr
                    key={row.id}
                    className="border-t border-brand-charcoal/10"
                  >
                    <td className="px-6 py-2">
                      {canManageTournaments &&
                      editingLeagueID === row.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="text"
                            value={leagueNameDraft}
                            onChange={(event) =>
                              setLeagueNameDraft(event.target.value)
                            }
                            placeholder={row.fileName || "League name"}
                            disabled={savingLeagueID === row.id}
                            aria-label="League name"
                            className="w-full max-w-xs px-2 py-1 border border-brand-charcoal/20 rounded-md bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                          />
                          <PrimaryButton
                            type="button"
                            className="!px-3 !py-1 text-sm"
                            loading={savingLeagueID === row.id}
                            onClick={() => handleLeagueNameSave(row)}
                          >
                            Save
                          </PrimaryButton>
                          <SecondaryButton
                            type="button"
                            className="!px-3 !py-1 text-sm"
                            onClick={cancelLeagueNameEdit}
                            disabled={savingLeagueID === row.id}
                          >
                            Cancel
                          </SecondaryButton>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{sheetLabel(row)}</span>
                          {canManageTournaments ? (
                            <SecondaryButton
                              type="button"
                              className="!px-3 !py-1 text-sm"
                              icon={FaEdit}
                              onClick={() => startLeagueNameEdit(row)}
                            >
                              Edit
                            </SecondaryButton>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-2 text-brand-muted">
                      {row.storagePath ? (
                        <button
                          type="button"
                          onClick={() =>
                            setSheetViewer({
                              title: sheetLabel(row),
                              subtitle: row.fileName,
                              storagePath: row.storagePath,
                              mimeType: row.mimeType,
                              fileName: row.fileName,
                            })
                          }
                          className="text-left text-brand-charcoal underline hover:text-brand-charcoal/80"
                        >
                          {row.fileName}
                        </button>
                      ) : (
                        row.fileName
                      )}
                      {row.pageCount ? ` · ${row.pageCount} pages` : ""}
                      {notice ? (
                        <p className="text-sm text-brand-charcoal mt-1 max-w-xl">
                          {notice}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-6 py-2">
                      <Badge variant={statusVariant(row, averageRows)}>
                        {statusLabel(row, averageRows)}
                      </Badge>
                    </td>
                    {canManageTournaments ? (
                      <td className="px-6 py-2 whitespace-nowrap">
                        <div className="flex gap-2 justify-end">
                          {canExtract &&
                          (row.status === "uploaded" ||
                            row.status === "failed" ||
                            sheetFoundNoAverages(row, averageRows)) ? (
                            <PrimaryButton
                              type="button"
                              className="!px-3 !py-1 text-sm"
                              loading={retryingID === row.id}
                              onClick={() => handleRetry(row)}
                            >
                              Extract again
                            </PrimaryButton>
                          ) : null}
                          <SecondaryButton
                            type="button"
                            className="!px-3 !py-1 text-sm"
                            onClick={() => handleRemove(row)}
                            disabled={removingID === row.id}
                          >
                            {removingID === row.id ? "Removing" : "Remove"}
                          </SecondaryButton>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card-board overflow-hidden min-w-0 max-w-full">
        <div className="px-6 py-4 border-b border-brand-charcoal/10">
          <h2 className="heading-display text-lg">Extracted averages</h2>
          <p className="text-sm text-brand-muted mt-1">
            {averageRows.length
              ? `${averageRows.length.toLocaleString("en-US")} AI-extracted averages. AI can make mistakes.`
              : imports.some((row) => sheetFoundNoAverages(row, averageRows))
              ? "Sheets were saved. No bowler averages were found on them."
              : "Averages from completed sheets show here."}
          </p>
        </div>
        {averageRows.length ? (
          <div className="px-4 sm:px-6 py-4 border-b border-brand-charcoal/10 space-y-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-brand-mist" />
              </div>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search any part of the name"
                className="block w-full pl-10 pr-9 py-2 border border-brand-charcoal/20 rounded-lg bg-white placeholder-brand-mist text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink sm:text-sm"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-brand-mist hover:text-brand-charcoal"
                  title="Clear search"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-brand-charcoal">
                <span className="font-medium">Games</span>
                <input
                  type="number"
                  min="1"
                  max="400"
                  value={gamesFilterMin}
                  onChange={(event) => setGamesFilterMin(event.target.value)}
                  className="w-20 px-2 py-1 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                />
              </label>
              {gamesFilters.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setGamesFilter(filter.id)}
                  className={chipClass(gamesFilter === filter.id)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            {sheetFilters.length > 2 ? (
              <div className="flex flex-wrap gap-2">
                {sheetFilters.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setSheetFilter(filter.id)}
                    className={chipClass(sheetFilter === filter.id)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-2 md:hidden">
              <span className="text-xs font-display font-bold uppercase tracking-wider text-brand-muted">
                Sort
              </span>
              {[
                { id: "firstName", label: "First name" },
                { id: "lastName", label: "Last name" },
                { id: "average", label: "Average" },
              ].map((field) => (
                <button
                  key={field.id}
                  type="button"
                  onClick={() => handleSortChange(field.id)}
                  className={chipClass(sortBy === field.id)}
                >
                  {field.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {visibleRows.length ? (
          <div className="max-w-full overflow-x-auto">
            <table className="min-w-[36rem] text-sm text-left">
              <thead>
                <tr>
                  <SortableTh
                    label="Name"
                    field="lastName"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSortChange}
                  />
                  <SortableTh
                    label="Average"
                    field="average"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSortChange}
                  />
                  <SortableTh
                    label="Games"
                    field="games"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSortChange}
                  />
                  <SortableTh
                    label="Pins"
                    field="pins"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSortChange}
                  />
                  <SortableTh
                    label="Source"
                    field="source"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSortChange}
                  />
                  <th className="px-4 py-2 text-left text-xs font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal">
                    <span className="sr-only">View</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => {
                  const underMinimum =
                    gamesFilter === "all" &&
                    !averageMeetsMinimum(row.games, minGamesForAverage);
                  return (
                  <tr
                    key={row.id}
                    className="border-t border-brand-charcoal/10"
                  >
                    <td className="px-4 py-2">{row.name}</td>
                    <td className="px-4 py-2">{row.average}</td>
                    <td className="px-4 py-2">
                      {row.games ?? "—"}
                      {row.gamesCalculated ? (
                        <span className="block text-xs text-brand-muted">
                          Calculated
                        </span>
                      ) : null}
                      {underMinimum ? (
                        <span className="block text-xs text-brand-muted">
                          Under {minGamesForAverage}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2">{row.pins ?? "—"}</td>
                    <td className="px-4 py-2">{row.source}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {row.storagePath ? (
                        <SoftButton
                          type="button"
                          size="xs"
                          title="View on sheet"
                          aria-label="View on sheet"
                          iconLeft={<FaEye className="h-3 w-3" />}
                          onClick={() => openSheetRow(row)}
                        >
                          View
                        </SoftButton>
                      ) : null}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : averageRows.length ? (
          <p className="px-6 py-6 text-sm text-brand-muted">
            No averages match that search or filter.
          </p>
        ) : null}
      </div>

      <LeagueSheetViewer
        isOpen={Boolean(sheetViewer)}
        onClose={() => setSheetViewer(null)}
        title={sheetViewer?.title}
        subtitle={sheetViewer?.subtitle}
        storagePath={sheetViewer?.storagePath}
        mimeType={sheetViewer?.mimeType}
        fileName={sheetViewer?.fileName}
        sourcePage={sheetViewer?.sourcePage}
        boundingBox={sheetViewer?.boundingBox}
        highlight={sheetViewer?.highlight}
        highlightAverage={sheetViewer?.highlightAverage}
        highlightGames={sheetViewer?.highlightGames}
        highlightPins={sheetViewer?.highlightPins}
      />
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete this average catalog?"
        message="This removes the catalog, its league sheets, and the extracted averages. Tournament averages that were already entered or verified stay."
        confirmText="Delete catalog"
        loading={deleting}
      />
    </div>
  );
};

export default AverageCatalogDetail;
